import { resolveGenderQuestionId, resolveLegacyGenderCode } from '@BUP/utils/sociogramGender'

// Characters Windows/macOS reject in file names, plus control characters.
// eslint-disable-next-line no-control-regex
const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g

/**
 * Suffix appended to each course-generator file name ("-<escuela>-<grado>-<año>") so files from
 * different schools/grades/years don't overwrite or get mixed up with each other in the
 * counselor's downloads folder, e.g. "01 Eleccion companeros-SSCC Manquehue-4-2026.csv".
 * The grade drops its "nivel:" prefix (':' isn't valid in Windows file names); the year is the
 * calendar year the period was opened (Chile's school year is the calendar year).
 * @param {Object} params
 * @param {string} params.organizationName
 * @param {string} params.grade - e.g. 'nivel:4', 'nivel:k'
 * @param {string} params.opensAt - ISO date the period was opened
 * @returns {string}
 */
export const buildCourseGeneratorFileSuffix = ({ organizationName, grade, opensAt }) => {
  const clean = (value) => String(value ?? '').replace(INVALID_FILE_NAME_CHARS, ' ').replace(/\s+/g, ' ').trim()
  const year = opensAt ? new Date(opensAt).getFullYear() : null
  return [
    clean(organizationName),
    clean(String(grade ?? '').replace(/^nivel:/, '')),
    year ? String(year) : ''
  ].filter(Boolean).map(part => `-${part}`).join('')
}

/**
 * Resolve each roster student's gender code ('H' / 'M' / '') for course-generator's
 * "04 Estudiantes genero.csv", from the current sociogram's surveys.
 *
 * A course-assignment period covers a whole nivel, i.e. several courses/sections, and each course
 * has its own sociogram survey (matched by the roster row's own `grade:<uuid>` tag - see
 * useSociogramGenderExport's doc comment for why it isn't the period's `nivel:N`). So this
 * resolves ONE SURVEY PER DISTINCT ROSTER GRADE and merges them - unlike useSociogramGenderExport,
 * which only resolves the first one and leaves every other section's gender blank (for SSCC
 * Manquehue, 4 sections per nivel, that's ~3 of every 4 students).
 *
 * Imperative (plain async, not hooks) because it runs on demand for whichever period row of the
 * history the counselor clicks, not for a period known at render time.
 * @param {Object} params
 * @param {Array<{enrollmentId: number, grade: string|null}>} params.roster
 * @param {import('axios').AxiosInstance} params.sociogramClient
 * @returns {Promise<{genderByEnrollmentId: Map<number, string>, gradesTotal: number, gradesResolved: number}>}
 */
export const resolveRosterGenders = async ({ roster, sociogramClient }) => {
  const rosterGrades = [...new Set(roster.map(student => student.grade).filter(Boolean))]
  const genderByEnrollmentId = new Map()
  if (!rosterGrades.length) return { genderByEnrollmentId, gradesTotal: 0, gradesResolved: 0 }

  const { data: current } = await sociogramClient.get('/sociograms/organization/current')
  const surveys = Array.isArray(current?.currentSociogram?.surveys) ? current.currentSociogram.surveys : []

  const results = await Promise.all(rosterGrades.map(async (grade) => {
    const survey = surveys.find(candidate => candidate.grade === grade)
    if (!survey) return false

    // Same two calls (and the same `?sociograms=<survey id>` param) as useGetTypeSurveyConfig /
    // useGetPreAnalyticData, which useSociogramGenderExport already relies on.
    const [{ data: typeSurveyConfig }, { data: preAnalyticData }] = await Promise.all([
      sociogramClient.get(`/type-survey-configs?sociograms=${survey.id}`),
      sociogramClient.get(`/survey-pre-analityc-formats?surveyId=${survey.id}`)
    ])
    const genderQuestionId = resolveGenderQuestionId(typeSurveyConfig?.[0]?.survey_template?.name)
    const content = preAnalyticData?.content
    if (!genderQuestionId || !Array.isArray(content)) return false

    content
      .filter(entry => entry.question_id === genderQuestionId)
      .forEach(entry => genderByEnrollmentId.set(entry.enrollmentId, resolveLegacyGenderCode(entry.answerId)))
    return true
  }))

  return {
    genderByEnrollmentId,
    gradesTotal: rosterGrades.length,
    gradesResolved: results.filter(Boolean).length
  }
}
